import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Save, DollarSign, Info, RotateCcw } from 'lucide-react';
import StepUpAuthModal, { getValidStepUpToken, PreviewSecurityBanner } from '../components/StepUpAuthModal';

const CAP_FIELDS = [
  { key: 'toll_charges_max', label: 'Toll Charges', icon: '🛣', desc: 'Maximum toll charges per booking' },
  { key: 'fuel_shortfall_max', label: 'Fuel Shortfall', icon: '⛽', desc: 'Maximum fuel shortfall charge per booking' },
  { key: 'cleaning_fee_max', label: 'Cleaning Fee', icon: '🧹', desc: 'Maximum cleaning fee per booking' },
  { key: 'excess_km_max', label: 'Excess Distance', icon: '📏', desc: 'Maximum excess km charge per booking' },
  { key: 'damage_estimate_max', label: 'Damage Estimate', icon: '🔧', desc: 'Maximum damage estimate per booking (requires admin review)' },
  { key: 'total_additional_charges_max', label: 'Total Additional Charges', icon: '📊', desc: 'Maximum total additional charges per booking' },
];

const DEFAULTS = {
  toll_charges_max: 300, fuel_shortfall_max: 400, cleaning_fee_max: 500,
  excess_km_max: 1500, damage_estimate_max: 5000, total_additional_charges_max: 6000,
};

export default function ChargeCapsPage() {
  const { api } = useAuth();
  const [caps, setCaps] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showStepUp, setShowStepUp] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/platform-owner/charge-caps');
        const c = res.data.charge_caps;
        setCaps({ ...c });
        setOriginal({ ...c });
        if (c.updated_at) setLastUpdated(c.updated_at);
      } catch { toast.error('Failed to load charge caps'); }
      setLoading(false);
    })();
  }, [api]);

  const hasChanges = JSON.stringify(caps) !== JSON.stringify(original);

  const _executeSave = async () => {
    // Validate
    for (const f of CAP_FIELDS) {
      const v = Number(caps[f.key]);
      if (isNaN(v) || v < 0) { toast.error(`${f.label} must be a positive number`); return; }
      if (v > 50000) { toast.error(`${f.label} cannot exceed $50,000`); return; }
    }
    setSaving(true);
    try {
      const payload = {};
      CAP_FIELDS.forEach(f => { payload[f.key] = Number(caps[f.key]); });
      const res = await api.put('/platform-owner/charge-caps', payload);
      const updated = res.data.charge_caps;
      setCaps({ ...updated });
      setOriginal({ ...updated });
      if (updated.updated_at) setLastUpdated(updated.updated_at);
      toast.success('Charge caps saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSave = () => {
    const existing = getValidStepUpToken();
    if (existing) {
      _executeSave();
    } else {
      setShowStepUp(true);
    }
  };

  const handleReset = () => {
    setCaps({ ...original });
  };

  return (
    <PlatformOwnerAppShell activeId="charge-caps" title="Charge Caps">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="charge-caps-page">
        <PreviewSecurityBanner />
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#004B8D]" />
                Charge Caps Configuration
              </CardTitle>
              <Badge variant="secondary" className="text-xs">
                {lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleDateString('en-AU')}` : 'Default values'}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Set maximum amounts for each charge type. Charges exceeding these caps require Platform Owner override approval.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="space-y-4">
                {[1,2,3,4,5,6].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}
              </div>
            ) : (
              <>
                {CAP_FIELDS.map(f => {
                  const isDefault = Number(caps[f.key]) === DEFAULTS[f.key];
                  const isChanged = Number(caps[f.key]) !== Number(original[f.key]);
                  return (
                    <div key={f.key} className="space-y-1.5" data-testid={`cap-field-${f.key}`}>
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <span>{f.label}</span>
                        {isDefault && <Badge variant="outline" className="text-[10px] py-0">Default</Badge>}
                        {isChanged && <Badge className="text-[10px] py-0 bg-amber-100 text-amber-700 border-amber-200">Modified</Badge>}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                        <Input
                          type="number"
                          min="0"
                          max="50000"
                          step="50"
                          value={caps[f.key] ?? ''}
                          onChange={e => setCaps(prev => ({ ...prev, [f.key]: e.target.value }))}
                          className="pl-7 text-sm"
                          data-testid={`cap-input-${f.key}`}
                        />
                      </div>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Info className="w-3 h-3" /> {f.desc}
                      </p>
                    </div>
                  );
                })}

                <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className="bg-[#004B8D] hover:bg-[#003a6d] flex-1 sm:flex-none"
                    data-testid="save-caps-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Charge Caps'}
                  </Button>
                  {hasChanges && (
                    <Button variant="outline" onClick={handleReset} data-testid="reset-caps-btn">
                      <RotateCcw className="w-4 h-4 mr-2" /> Discard Changes
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <StepUpAuthModal
        isOpen={showStepUp}
        onSuccess={() => { setShowStepUp(false); _executeSave(); }}
        onCancel={() => setShowStepUp(false)}
        actionLabel="change financial settings"
      />
    </PlatformOwnerAppShell>
  );
}
